import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { productService } from '../../services/productService';
import { Product } from '../../types/product';

const ProductList = ({ onEdit, onAdd }: { onEdit: (product: Product) => void, onAdd: () => void }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  const categories = [
    { id: 'all', name: 'Todos' },
    { id: 'spares', name: 'Repuestos' },
    { id: 'machinery', name: 'Maquinaria' },
    { id: 'tools', name: 'Herramientas' },
    { id: 'safety', name: 'Seguridad' },
    { id: 'automation', name: 'Automatización' }
  ];

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    const allProducts = await productService.getProducts();
    setProducts(allProducts);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar este producto?')) {
      const success = await productService.deleteProduct(id);
      if (success) {
        loadProducts();
      }
    }
  };

  const toggleFeatured = async (id: number) => {
    const product = await productService.getProductById(id);
    if (product) {
      const success = await productService.updateProduct(id, { featured: !product.featured });
      if (success) {
        loadProducts();
      }
    }
  };

  const toggleStock = async (id: number) => {
    const product = await productService.getProductById(id);
    if (product) {
      const success = await productService.updateProduct(id, { inStock: !product.inStock });
      if (success) {
        loadProducts();
      }
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
    const matchesFeatured = !showFeaturedOnly || product.featured;
    
    return matchesSearch && matchesCategory && matchesFeatured;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gestión de Productos</h2>
        <Button onClick={onAdd} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Agregar Producto
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="featured-only"
              checked={showFeaturedOnly}
              onChange={(e) => setShowFeaturedOnly(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="featured-only" className="text-sm text-gray-700">
              Solo destacados
            </label>
          </div>

          <div className="text-sm text-gray-500 flex items-center">
            Total: {filteredProducts.length} productos
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {/* Vista de tabla para pantallas grandes */}
        <div className="hidden lg:block">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] table-auto">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                    Producto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                    Categoría
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                    Precio
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px]">
                    Stock
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-10 h-10 rounded-lg object-cover mr-3 flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate" title={product.name}>
                            {product.name}
                          </div>
                          <div className="text-sm text-gray-500 truncate" title={product.description}>
                            {product.description.length > 50 ? `${product.description.substring(0, 50)}...` : product.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full block truncate" title={categories.find(c => c.id === product.category)?.name || product.category}>
                        {categories.find(c => c.id === product.category)?.name || product.category}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 truncate">
                      ${product.price.toFixed(2)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <span className={`text-sm font-medium ${
                          product.stock > 10 ? 'text-green-600' :
                          product.stock > 0 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {product.stock} unidades
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        <button
                          onClick={() => toggleStock(product.id)}
                          className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                            product.inStock
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {product.inStock ? 'En stock' : 'Agotado'}
                        </button>
                        <button
                          onClick={() => toggleFeatured(product.id)}
                          className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                            product.featured
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {product.featured ? 'Destacado' : 'Normal'}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm font-medium">
                      <div className="flex justify-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEdit(product)}
                          className="flex-shrink-0 min-h-[38px] px-3"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(product.id)}
                          className="text-red-600 hover:text-red-800 hover:border-red-300 flex-shrink-0 min-h-[38px] px-3"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Vista de tarjetas para pantallas pequeñas y medianas */}
        <div className="lg:hidden">
          <div className="divide-y divide-gray-200">
            {filteredProducts.map((product) => (
              <div key={product.id} className="p-3 sm:p-4 hover:bg-gray-50">
                <div className="flex items-start space-x-3">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {product.name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 mt-1 line-clamp-2">
                      {product.description}
                    </p>
                    
                    <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-1 sm:gap-2">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
                        {categories.find(c => c.id === product.category)?.name || product.category}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        ${product.price.toFixed(2)}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        product.stock > 10 ? 'bg-green-100 text-green-600' :
                        product.stock > 0 ? 'bg-yellow-100 text-yellow-600' : 'bg-red-100 text-red-600'
                      }`}>
                        Stock: {product.stock}
                      </span>
                    </div>

                    <div className="mt-2 sm:mt-3 flex flex-wrap gap-1 sm:gap-2">
                      <button
                        onClick={() => toggleStock(product.id)}
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          product.inStock
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {product.inStock ? 'En stock' : 'Agotado'}
                      </button>
                      <button
                        onClick={() => toggleFeatured(product.id)}
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          product.featured
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {product.featured ? 'Destacado' : 'Normal'}
                      </button>
                    </div>

                    <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(product)}
                        className="flex-1 min-h-[44px] sm:min-h-[38px]"
                      >
                        <Edit className="w-4 h-4 mr-1 sm:mr-2" />
                        <span className="text-xs sm:text-sm">Editar</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(product.id)}
                        className="text-red-600 hover:text-red-800 hover:border-red-300 min-h-[44px] sm:min-h-[38px]"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="sr-only sm:not-sr-only sm:ml-2 text-xs sm:text-sm">Eliminar</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">No se encontraron productos</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;